import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { getOptimizationTargetKey } from "./predictUtils";
import {
  formatSuggestedSettings,
  formatUnitForDisplay,
} from "../../utils/Utils";
import { hasAssignedLicensePermission } from "../../utils/LicenseUtils";
import { truncateFloat } from "../../utils/appUtils";
import { APP_SCOPE } from "../../constants/global.constants";
import { LICENSE_PERMISSION_IDS } from "../../constants/LicenseManagement.constants";

import { getRouteAppScope } from "../../network/networkUtil";

const ExportAllData = () => {
  const { t } = useTranslation();
  const cips = useSelector((state) => state.cips);
  const cleanData = useSelector((state) => state.cleanData);
  const cleaningEffectiness = useSelector((state) => state.cleaningEffectiness);
  const fouling = useSelector((state) => state.fouling);
  const flow = useSelector((state) => state.flow);
  const pressure = useSelector((state) => state.pressure);
  const water_type = useSelector((state) => state.water_type);
  const feed = useSelector((state) => state.feed_quality);
  const permeate = useSelector((state) => state.permeate_quality);
  const temperature = useSelector((state) => state.temperature);
  const volume = useSelector((state) => state.volume);
  const currency = useSelector((state) => state.currency);
  const mass = useSelector((state) => state.mass);
  const conversion_rate = useSelector((state) => state.conversion_rate);
  const feed_quality = useSelector((state) => state.feed_quality);
  const permeate_quality = useSelector((state) => state.permeate_quality);
  const engryPrice = useSelector(
    (state) => state.trainUtilityCosts.energy_price,
  );
  const cipWaterPrice = useSelector(
    (state) => state.trainUtilityCosts?.water_price,
  );
  const costofDownTime = useSelector(
    (state) => state.trainUtilityCosts?.downtime_cost,
  );
  const alkali_used_per_cip = useSelector((state) => state.alkali_used_per_cip);
  const acid_used_per_cip = useSelector((state) => state.acid_used_per_cip);
  const acid_price = useSelector((state) => state.acid_price);
  const alkali_price = useSelector((state) => state.alkali_price);
  const additive_alkali_price = useSelector(
    (state) => state.additive_alkali_price,
  );
  const additive_acid_price = useSelector((state) => state.additive_acid_price);
  const acid_chemical_name = useSelector((state) => state.acid_chemical_name);
  const alkali_chemical_name = useSelector((state) => state.alkali_chemical_name);
  const additive_alkali_chemical_name = useSelector(
    (state) => state.additive_alkali_chemical_name,
  );
  const additive_acid_chemical_name = useSelector(
    (state) => state.additive_acid_chemical_name,
  );
  const additive_alkali_used_per_cip = useSelector(
    (state) => state.additive_alkali_used_per_cip,
  );
  const additive_acid_used_per_cip = useSelector(
    (state) => state.additive_acid_used_per_cip,
  );
  const acid_cip_ph = useSelector((state) => state.acid_cip_ph);
  const additive_acid_ph = useSelector((state) => state.additive_acid_ph);
  const alkali_cip_ph = useSelector((state) => state.alkali_cip_ph);
  const additive_alkali_ph = useSelector((state) => state.additive_alkali_ph);
  const acid_cip_temp = useSelector((state) => state.acid_cip_temp);
  const additive_acid_temp = useSelector((state) => state.additive_acid_temp);
  const alkali_cip_temp = useSelector((state) => state.alkali_cip_temp);
  const additive_alkali_temp = useSelector(
    (state) => state.additive_alkali_temp,
  );
  const acid_cip_duration = useSelector((state) => state.acid_cip_duration);
  const additive_acid_duration = useSelector(
    (state) => state.additive_acid_duration,
  );
  const alkali_cip_duration = useSelector((state) => state.alkali_cip_duration);
  const additive_alkali_duration = useSelector(
    (state) => state.additive_alkali_duration,
  );
  const plantName = useSelector((state) => state.plantName);
  const plantLocation = useSelector((state) => state.plantLocation);
  const num_trains = useSelector((state) => state.num_trains);
  const dataset_name = useSelector((state) => state.dataset_name);
  const tol_num_elements = useSelector((state) => state.tol_num_elements);
  const is_dupont_element = useSelector((state) => state.is_dupont_element);
  const sel_pass = useSelector((state) => state.sel_pass);
  const sel_train = useSelector((state) => state.sel_train);
  const sel_stage = useSelector((state) => state.sel_stage);
  const sel_pressure_vessels = useSelector(
    (state) => state.sel_pressure_vessels,
  );
  const sel_element_involved = useSelector(
    (state) => state.sel_element_involved,
  );
  const sel_element_type = useSelector((state) => state.sel_element_type);
  const feed_water_factor = useSelector((state) => state.feed_water_factor);
  const feed_water_permeate_factor = useSelector(
    (state) => state.feed_water_permeate_factor,
  );
  const {
    n_neighbors: clusterPoints,
    window: medianCurvePoints,
    n: maxRelativeDistance,
    pf_threshold: permeateFlowDropThreshold,
    pressure_drop_threshold: pressureDropThreshold,
    pressure_drop_abs_threshold: pressureDropThresholdAbsolute,
    salt_passage_threshold: saltPassageIncreaseThreshold,
    min_day_between_cips: minDaysBetweenCIPs,
    max_days_between_cips: maxDaysBetweenCIPs,
    k: cipIdentificationResolution,
    shutdown_length: minIdleDaysShutdown,
    use_permeability_trigger: usePermeabilityTrigger,
    permeability_only: permeabilityOnly,
    permeability_threshold: permeabilityThreshold,
    permeability_threshold_predict: permeabilityThresholdPredict,
    use_salt_passage: useSaltPassageChange,
    min_time: minStabilizationDaysAfterCIP,
    max_time: maxStabilizationDaysAfterCIP,
    min_num_points: minPointsForCleaningEffectiveness,
    stabilization_days: deafultStabilizationDaysAfterCIP,
    top_exp: foulingAggressivenessFactor,
    max_evals: numberOfIterationsToFindOptima,
    adapt_pf_trend: adaptPermeateFlowDropThreshold,
    adapt_pressure_drop_trend: adaptPressureDropThreshold,
    adapt_salt_passage_trend: adaptSaltPassageThreshold,
    simulation_improve_accuracy: simulationImproveAccuracy,
    failure_probability_threshold: failureProbabilityThreshold,
    optimization_target: optimizationTarget,
  } = useSelector((state) => state.advancedSettings);

  const installation_date = useSelector((state) => state.installation_date);
  const warranty = useSelector((state) => state.warranty);
  const suggestedSettings = useSelector((state) => state.suggestedSettings);
  const isRAFlow = getRouteAppScope() === APP_SCOPE.RA;
  const userLicenses = useSelector((state) => state.userLicenses || []);
  const hasPermeabilityPermissionAnyStatus = hasAssignedLicensePermission(
    userLicenses,
    LICENSE_PERMISSION_IDS.USE_PERMEABILITY,
  );

  const formatAdvancedSettingForExcel = (value) => {
    if (value === null || value === undefined || value === "") return value;
    const numericValue = Number(value);
    return Number.isFinite(numericValue)
      ? truncateFloat(numericValue, 2).toFixed(2)
      : value;
  };

  // Format suggested settings for Excel export
  const suggestedOthers = formatSuggestedSettings(suggestedSettings, t);

  const exportAllData = {
    userCips: cips?.filter((item) => !item.is_tool_identified),
    cips,
    cleaningEffectiness,
    fouling,
    cleanData,
    units: {
      flow: flow,
      pressure: pressure,
      feed_quality: feed_quality,
      permeate_quality: permeate_quality,
      temperature: temperature,
    },
    others: [
      {
        plantName,
        plantLocation,
        water_type,
        num_trains,
        tol_num_elements,
        is_dupont_element: is_dupont_element
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        dataset_name,
        ...(isRAFlow && {
          installation_date: installation_date,
          warranty: warranty,
        }),
        sel_train,
        sel_pass,
        sel_stage,
        sel_pressure_vessels,
        sel_element_involved,
        element_type: sel_element_type,
        flow,
        pressure,
        feed,
        feed_water_factor,
        permeate,
        feed_water_permeate_factor,
        temperature: formatUnitForDisplay(temperature),
        volume,
        currency,
        mass,
        exchangeRate: conversion_rate,
        cipWaterPrice,
        costofDownTime,
        engryPrice,
        alkali_chemical_name,
        alkali_price,
        alkali_used_per_cip,
        alkali_cip_ph,
        alkali_cip_temp,
        alkali_cip_duration,
        additive_alkali_chemical_name,
        additive_alkali_price,
        additive_alkali_used_per_cip,
        additive_alkali_ph,
        additive_alkali_temp,
        additive_alkali_duration,
        acid_chemical_name,
        acid_price,
        acid_used_per_cip,
        acid_cip_ph,
        acid_cip_temp,
        acid_cip_duration,
        additive_acid_chemical_name,
        additive_acid_price,
        additive_acid_used_per_cip,
        additive_acid_ph,
        additive_acid_temp,
        additive_acid_duration,
        clusterPoints: formatAdvancedSettingForExcel(clusterPoints),
        medianCurvePoints: formatAdvancedSettingForExcel(medianCurvePoints),
        maxRelativeDistance: formatAdvancedSettingForExcel(maxRelativeDistance),
        permeateFlowDropThreshold: formatAdvancedSettingForExcel(
          permeateFlowDropThreshold,
        ),
        pressureDropThreshold: formatAdvancedSettingForExcel(
          pressureDropThreshold,
        ),
        pressureDropThresholdAbsolute: formatAdvancedSettingForExcel(
          pressureDropThresholdAbsolute,
        ),
        useSaltPassageChange: useSaltPassageChange
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        saltPassageIncreaseThreshold: formatAdvancedSettingForExcel(
          saltPassageIncreaseThreshold,
        ),
        minDaysBetweenCIPs: formatAdvancedSettingForExcel(minDaysBetweenCIPs),
        maxDaysBetweenCIPs: formatAdvancedSettingForExcel(maxDaysBetweenCIPs),
        cipIdentificationResolution: formatAdvancedSettingForExcel(
          cipIdentificationResolution,
        ),
        minIdleDaysShutdown: formatAdvancedSettingForExcel(minIdleDaysShutdown),
        ...(hasPermeabilityPermissionAnyStatus && {
          usePermeabilityTrigger: usePermeabilityTrigger
            ? t("ValueStrings.yes")
            : t("ValueStrings.no"),
          permeabilityOnly: permeabilityOnly
            ? t("ValueStrings.yes")
            : t("ValueStrings.no"),
          permeabilityThreshold: formatAdvancedSettingForExcel(
            permeabilityThreshold,
          ),
          permeabilityThresholdPredict: formatAdvancedSettingForExcel(
            permeabilityThresholdPredict,
          ),
        }),
        maxStabilizationDaysAfterCIP: formatAdvancedSettingForExcel(
          maxStabilizationDaysAfterCIP,
        ),
        minStabilizationDaysAfterCIP: formatAdvancedSettingForExcel(
          minStabilizationDaysAfterCIP,
        ),
        minPointsForCleaningEffectiveness: formatAdvancedSettingForExcel(
          minPointsForCleaningEffectiveness,
        ),
        deafultStabilizationDaysAfterCIP: formatAdvancedSettingForExcel(
          deafultStabilizationDaysAfterCIP,
        ),
        foulingAggressivenessFactor: formatAdvancedSettingForExcel(
          foulingAggressivenessFactor,
        ),
        numberOfIterationsToFindOptima: formatAdvancedSettingForExcel(
          numberOfIterationsToFindOptima,
        ),
        adaptPermeateFlowDropThreshold: adaptPermeateFlowDropThreshold
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        adaptPressureDropThreshold: adaptPressureDropThreshold
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        adaptSaltPassageThreshold: adaptSaltPassageThreshold
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        simulationImproveAccuracy: simulationImproveAccuracy
          ? t("ValueStrings.yes")
          : t("ValueStrings.no"),
        failureProbabilityThreshold: formatAdvancedSettingForExcel(
          failureProbabilityThreshold,
        ),
        optimizationTarget: t(
          `advanceSettings.${getOptimizationTargetKey(optimizationTarget)}`,
        ),
      }, //keep the order in sync with OthersTableStrings
    ],
    suggestedOthers,
  };

  return exportAllData;
};

export default ExportAllData;
